import { createContext, type PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useAdminSettings } from './api';
import { type StudentGroupRecord } from '../../../models/types';
import { type ClassInfo, type MyDetails, type RosterTableData } from '../components/roster/types';
import { CLASS_INFO, CLASS_ME, CLASS_PERMISSIONS, CLASS_STUDENTS } from '../config/apiPaths';
import { type SchoolSettings } from '../types/apiTypes';
import { getUserUuidHeaders } from '../utils/httpUtils';
import { getUrls } from '../utils/url';

export interface RosterProviderProps {
    classUuid: string;
    classId: string | null;
    userUuid: string | null;
    isDemo: string | null;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface RosterContextInterface {
    classUuid: string;
    classId: string | null;
    loading: boolean;
    fetchingStudents: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    roster: any[];
    selectedStudentId: number | undefined;
    setSelectedStudentId: (selectedUser: number) => void;
    openChangeHostModal: boolean;
    setOpenChangeHostModal: (changeHostModal: boolean) => void;
    openEditModal: boolean;
    setOpenEditModal: (openEditModal: boolean) => void;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openDeleteModal: boolean;
    setOpenDeleteModal: (openEditModal: boolean) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    rosterData: { loading: boolean; roster: RosterTableData[] };
    setRosterData: (rosterData: any) => void;
    showAddStudentsButton: boolean;
    userUuid: string | null;
    isDemo: string | null;
    isAuthorized: boolean | null;
    myDetails?: MyDetails;
    adminSettings?: SchoolSettings;
    sendingCalendarInvite: boolean;
    setSendingCalendarInvite: (sendingCalendarInvite: boolean) => void;
    loadRoster: () => void;
    classInfo?: ClassInfo;
    studentGroups?: StudentGroupRecord[];
    onBack?: () => void;
}

const RosterContext = createContext(undefined as unknown as RosterContextInterface);

const { Provider, Consumer } = RosterContext;

const RosterProvider = (props: PropsWithChildren<RosterProviderProps>) => {
    const { classId, classUuid, userUuid, isDemo, narrowStyle, fullScreen, onBack, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [rosterData, setRosterData] = useState<RosterContextInterface['rosterData']>({ loading: true, roster: [] });
    const [openChangeHostModal, setOpenChangeHostModal] = useState<boolean>(false);
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [selectedStudentId, setSelectedStudentId] = useState<number | undefined>(undefined);

    const [sendingCalendarInvite, setSendingCalendarInvite] = useState<boolean>(false);

    const [studentGroups, setStudentGroups] = useState<StudentGroupRecord[]>([]);
    const { api } = getUrls();

    const { data: adminSettings } = useAdminSettings();

    /**
     * Details are only pulled for members of a class.
     * Admins do not have student details in a class.
     */
    const { data: myDetails } = useQuery({
        queryKey: [CLASS_ME, classId],
        queryFn: async () => {
            const { data } = await axios.get<MyDetails>(`${api}${CLASS_ME}?class_id=${classId}`, {
                headers: getUserUuidHeaders(userUuid as string)
            });
            return data;
        }
    });

    const { data: isAuthorized } = useQuery({
        queryKey: [CLASS_PERMISSIONS, classUuid],
        enabled: !!classUuid && !!userUuid,
        initialData: null,
        queryFn: async () => {
            if (!classUuid || !userUuid) {
                return false;
            }

            try {
                const {
                    data: { isPrivileged }
                } = await axios.get(`${api}${CLASS_PERMISSIONS}?class_id=${classUuid}`, {
                    headers: getUserUuidHeaders(userUuid)
                });

                return isPrivileged;
            } catch (err) {
                console.error(err);
                return false;
            }
        }
    });

    const {
        data: initialRosterData,
        refetch: loadRoster,
        isFetching: fetchingStudents
    } = useQuery({
        queryKey: [CLASS_STUDENTS, classUuid],
        enabled: !!classUuid && !!isAuthorized,
        queryFn: async () => {
            if (!classUuid || !isAuthorized) {
                return null;
            }

            const { data } = await axios.get<RosterTableData[]>(`${api}${CLASS_STUDENTS}?class_id=${classUuid}`);
            return data;
        }
    });

    useEffect(() => {
        if (!initialRosterData) {
            return;
        }

        setRosterData({ roster: initialRosterData, loading: false });
        setIsLoading(false);
    }, [initialRosterData]);

    const { data: classInfo } = useQuery({
        queryKey: [CLASS_INFO, classUuid],
        enabled: !!classUuid && !!isAuthorized,
        queryFn: async () => {
            if (!classUuid || !isAuthorized) {
                return null;
            }

            const { data } = await axios.get(`${api}${CLASS_INFO}?class_id=${classUuid}&with_info=true`);
            const {
                student_groups: studentGroupsArray,
                class_name: className,
                status,
                sid,
                meeting_id: meetingId,
                data: classData
            } = data;

            setStudentGroups(studentGroupsArray);

            const isEmpty = Object.keys(classData).length === 0;
            const showAddStudentsButton = !isEmpty;

            return { class_name: className, sid, meeting_id: meetingId, showAddStudentsButton, status };
        }
    });

    const { showAddStudentsButton } = classInfo || { showAddStudentsButton: false };

    useEffect(() => {
        if (isAuthorized === null) {
            return;
        }

        if (isAuthorized === false) {
            setIsLoading(false);
            setRosterData({ roster: [], loading: false });
        }
    }, [classInfo, isAuthorized]);

    return useMemo(() => {
        const value: RosterContextInterface = {
            classId,
            classUuid,
            loading: rosterData.loading || isLoading,
            fetchingStudents,
            isFullScreen: !!fullScreen,
            isNarrowStyle: !!narrowStyle,
            roster: rosterData.roster,
            openChangeHostModal,
            setOpenChangeHostModal,
            openEditModal,
            setOpenEditModal,
            openDeleteModal,
            setOpenDeleteModal,
            openErrorModal,
            setOpenErrorModal,
            errorModalMessage,
            setErrorModalMessage,
            selectedStudentId,
            setSelectedStudentId,
            rosterData,
            setRosterData,
            showAddStudentsButton,
            userUuid,
            isDemo,
            isAuthorized,
            myDetails,
            adminSettings,
            sendingCalendarInvite,
            setSendingCalendarInvite,
            loadRoster,
            classInfo: classInfo || undefined,
            studentGroups,
            onBack
        };

        return <Provider value={value}>{children}</Provider>;
    }, [
        adminSettings,
        children,
        classId,
        classInfo,
        classUuid,
        errorModalMessage,
        fullScreen,
        isAuthorized,
        isDemo,
        isLoading,
        fetchingStudents,
        loadRoster,
        myDetails,
        narrowStyle,
        onBack,
        openChangeHostModal,
        openDeleteModal,
        openEditModal,
        openErrorModal,
        rosterData,
        selectedStudentId,
        sendingCalendarInvite,
        showAddStudentsButton,
        studentGroups,
        userUuid
    ]);
};

export { RosterContext, RosterProvider, Consumer as RosterConsumer };
